"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import {
  LogOut,
  ChevronDown,
  Loader2,
  Shield,
  LayoutDashboard,
  KeyRound,
} from "lucide-react";
import Link from "next/link";
import { useAuth } from "@/lib/auth-context";
import { useLocale, useT } from "@/lib/use-i18n";
import { localePath } from "@/lib/i18n/locales";
import { getUserAvatarUrl, getUserInitials } from "@/lib/user-avatar";
import {
  computePopoverPosition,
  type PopoverPosition,
} from "@/lib/popover-position";

function UserAvatar({
  avatarUrl,
  initials,
  sizeClass = "size-7",
}: {
  avatarUrl: string | null;
  initials: string;
  sizeClass?: string;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = Boolean(avatarUrl) && !imgFailed;

  return (
    <div
      className={`relative flex ${sizeClass} shrink-0 items-center justify-center overflow-hidden rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--background-elevated)] text-[11px] font-semibold tracking-normal text-foreground-soft`}
      // 2026-10-03 RUN12 FASE 8 N13 [usermenu-tracking-normal] (research 2026 W3C CJK: wide 0.025em→normal Δ0.275px@11px; 11px piso intacto).
      data-testid="user-avatar"
    >
      {showImage ? (
        // 2026-09-27 EXENCIÓN next/image documentada: avatar remoto OAuth
        // (Google/Supabase, dominio variable — exigiría remotePatterns abierto)
        // + above-fold en el header (eager intencional, sin lazy); <img> con
        // width/height anti-CLS + decoding async es la vía mínima.
        // eslint-disable-next-line @next/next/no-img-element -- OAuth avatar URL from Google/Supabase
        <img
          src={avatarUrl ?? undefined}
          alt=""
          width={28}
          height={28}
          decoding="async"
          referrerPolicy="no-referrer"
          className="size-full object-cover"
          data-testid="user-avatar-image"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span data-testid="user-avatar-initials">{initials}</span>
      )}
    </div>
  );
}

/**
 * Account menu — portaled + position:fixed under the trigger.
 * Absolute top-full inside sticky liquid-glass header fails after scroll
 * (backdrop-filter creates a containing block; menu opens off-screen).
 */
export function UserMenu() {
  const { user, isAdmin, signOut, hasPassword } = useAuth();
  const router = useRouter();
  const t = useT();
  // 2026-09-28 D4-09: el sign-out iba a `router.push("/")` sin prefijo de
  // locale ⇒ el usuario salía a la home EN (y en `ar` perdía `dir="rtl"`).
  // research 2026-09-28 AGENTS.md § i18n (SSOT locale-routes) + Google Search
  // Central localized-versions.
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [coords, setCoords] = useState<PopoverPosition>({
    top: 0,
    left: 0,
    right: 0,
  });
  const [mounted] = useState(() => typeof document !== "undefined");
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateCoords = () => {
    const btn = triggerRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const menuWidth = dropdownRef.current?.offsetWidth || 240;
    const menuHeight = dropdownRef.current?.offsetHeight || 360;
    setCoords(computePopoverPosition(r, menuWidth, menuHeight, "end"));
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateCoords();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updateCoords();
    window.addEventListener("resize", onReposition);
    // Keep menu glued to the sticky bar while scrolling (viewport coords).
    window.addEventListener("scroll", onReposition, {
      passive: true,
      capture: true,
    });
    window.visualViewport?.addEventListener("resize", onReposition);
    window.visualViewport?.addEventListener("scroll", onReposition);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
      window.visualViewport?.removeEventListener("resize", onReposition);
      window.visualViewport?.removeEventListener("scroll", onReposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    if (menu) {
      const first = menu.querySelector<HTMLElement>(
        "a[href], button:not([disabled])",
      );
      first?.focus();
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key === "Tab") {
        const menuEl = menuRef.current;
        if (!menuEl) return;
        const focusable = menuEl.querySelectorAll<HTMLElement>(
          "a[href], button:not([disabled])",
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  if (!user) return null;

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
      setOpen(false);
      router.push(localePath(locale, "/"));
      router.refresh();
    } finally {
      setSigningOut(false);
    }
  };

  const initials = getUserInitials(user.email);
  const avatarUrl = getUserAvatarUrl(user);

  const menu =
    open && mounted
      ? createPortal(
          <>
            {/* 2026-09-22 FASE 3 FIX P3 a11y: sin onKeyDown muerto en backdrop
                (div role=presentation no focuseable — Escape ya lo maneja el
                listener de documento en useEffect; handler duplicado = ruido). */}
            <div
              className="fixed inset-0 z-[80] bg-black/55"
              onClick={() => setOpen(false)}
              role="presentation"
              data-testid="user-menu-backdrop"
            />
            <div
              ref={dropdownRef}
              id="user-menu"
              role="menu"
              aria-label={t("userMenuToggle")}
              style={{
                position: "fixed",
                top: coords.top,
                right: coords.right,
              }}
              // 2026-09-12: max-h + scroll — ~360px de alto tapaba landscape sin scroll interno.
              className="chrome-dropdown-menu z-[81] w-60 max-w-[calc(100vw-1rem)] max-h-[70dvh] max-h-[min(70dvh,380px)] overflow-y-auto overscroll-contain rounded-[var(--radius-card)] border border-[var(--border-strong)] bg-[var(--background-elevated)] v-rise"
            >
              <div ref={menuRef}>
                <div className="flex items-center gap-3 border-b border-border px-4 py-3.5">
                  <UserAvatar
                    avatarUrl={avatarUrl}
                    initials={initials}
                    sizeClass="size-9"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="eyebrow-text mb-1">
                      {t("userSessionActive")}
                    </p>
                    <p
                      className="truncate text-[13px] font-medium text-foreground"
                      title={user.email ?? undefined}
                    >
                      {user.email}
                    </p>
                  </div>
                </div>
                <div className="py-1">
                  <Link
                    href="/dashboard"
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    // 2026-10-01 RUN11 A-01: focus-visible (menú con trap Tab
                    // :139-154 — sin anillo el foco de teclado era invisible).
                    className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-foreground-soft transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                  >
                    <LayoutDashboard className="size-4" aria-hidden="true" />
                    {t("navDashboard")}
                  </Link>
                  {hasPassword ? (
                    <Link
                      href="/update-password"
                      role="menuitem"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-foreground-soft transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                    >
                      <KeyRound className="size-4" aria-hidden="true" />
                      {t("userUpdatePassword")}
                    </Link>
                  ) : null}
                  {isAdmin ? (
                    <Link
                      href="/admin"
                      role="menuitem"
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-foreground-soft transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                    >
                      <Shield className="size-4" aria-hidden="true" />
                      {t("userAdminPanel")}
                    </Link>
                  ) : null}
                </div>
                <div className="border-t border-border py-1">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleSignOut}
                    disabled={signingOut}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-[13px] text-[var(--danger)] transition-colors hover:bg-[var(--danger)]/8 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {signingOut ? (
                      <Loader2
                        className="size-4 animate-spin motion-reduce:animate-none"
                        aria-hidden="true"
                      />
                    ) : (
                      <LogOut className="size-4" aria-hidden="true" />
                    )}
                    {t("userSignOut")}
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )
      : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (!open) updateCoords();
          setOpen((p) => !p);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="user-menu"
        aria-label={t("userMenuToggle")}
        // 2026-09-23 FASE 3 FIX diseño (research 2026 DSP single-source radius:
        // !rounded/!min-h-9 con !important rompían la cascada — .lang-selector-trigger
        // SSOT ya es pill 9999 + min-h 40px; sin overrides, misma forma que LanguageSelector).
        className="lang-selector-trigger gap-1.5 px-1.5"
      >
        <UserAvatar avatarUrl={avatarUrl} initials={initials} />
        <ChevronDown
          className="size-3 text-muted-foreground"
          aria-hidden="true"
        />
      </button>
      {menu}
    </div>
  );
}
